/*
  Background clip in the 850 hero.

  Most of the work happens in the markup, not here. iOS grants inline autoplay
  by reading `muted` and `playsinline` off the <video> as it parses, so the
  section writes both out by hand; anything this file set afterwards would
  arrive too late to matter. What it does set, it sets defensively, for the
  theme editor's re-renders.

  It autoplays for everyone, including visitors whose system asks for reduced
  motion. That is deliberate: an earlier version honoured the preference and
  parked on the poster, which meant the hero sat still on any machine with
  Windows animation effects switched off, and read as broken rather than as
  considerate. The pause control carries the accessibility side instead - the
  clip loops indefinitely, and WCAG 2.2.2 wants a way to stop moving content
  the visitor did not start.

  iOS still refuses autoplay outright in Low Power Mode, and no markup gets
  around that. When the refusal comes back, the clip starts on the visitor's
  first touch or scroll instead, which is the closest thing to automatic that
  the platform allows.
*/

(function () {
  'use strict';

  const GESTURES = ['touchstart', 'pointerdown', 'keydown', 'scroll'];

  function bind(root) {
    const video = root.querySelector('video');
    if (!video || root.dataset.heroVideoReady) return;
    root.dataset.heroVideoReady = 'true';

    video.muted = true;
    video.playsInline = true;
    video.loop = true;

    let button = root.querySelector('[data-hero-850-pause]');

    // Belt and braces with the (hover: none) rule in 850.css. That rule is the
    // right way to say "this is a touch device", but it is one signal, and a
    // device that reports its hover capability wrongly - iPad with a pointer
    // attached, Safari in desktop-site mode - would leave the control sitting
    // on the artwork. maxTouchPoints is an independent signal, so anything that
    // reports touch loses the button outright rather than merely hiding it.
    // This does take the WCAG 2.2.2 stop out of the accessibility tree on those
    // devices, which the CSS-only version kept. Deliberate, and the cost of
    // wanting the control gone for certain.
    if (button && navigator.maxTouchPoints > 0) {
      button.remove();
      button = null;
    }

    const label = button && button.querySelector('[data-hero-850-pause-label]');
    let stoppedByVisitor = false;
    let waitingForGesture = false;

    // The element is the source of truth: play() is async and can be refused
    // outright, so rendering the button when we *ask* for playback gets it wrong.
    function sync() {
      const playing = !video.paused;
      root.classList.toggle('hero-850--still', !playing);
      if (!button) return;
      button.setAttribute('aria-pressed', playing ? 'false' : 'true');
      if (label) label.textContent = playing ? button.dataset.labelPause : button.dataset.labelPlay;
    }

    function onGesture() {
      if (stoppedByVisitor) return releaseGesture();
      const played = video.play();
      if (played && typeof played.catch === 'function') {
        played.then(releaseGesture).catch(function () {});
      } else {
        releaseGesture();
      }
    }

    function releaseGesture() {
      if (!waitingForGesture) return;
      waitingForGesture = false;
      GESTURES.forEach(function (type) {
        document.removeEventListener(type, onGesture);
      });
    }

    function waitForGesture() {
      if (waitingForGesture || stoppedByVisitor) return;
      waitingForGesture = true;
      GESTURES.forEach(function (type) {
        document.addEventListener(type, onGesture, { passive: true });
      });
    }

    function attempt() {
      const played = video.play();
      if (played && typeof played.catch === 'function') {
        played.catch(function () {
          // Low Power Mode, Low Data Mode, or a data-saver setting. Fall back
          // to the visitor's first gesture and leave the poster up until then.
          waitForGesture();
          sync();
        });
      }
    }

    video.addEventListener('play', sync);
    video.addEventListener('pause', sync);

    // At bind time the element may still be at readyState 0, and a play() call
    // that early can come back refused for no reason other than that there is
    // nothing decoded yet. Try again at each point the browser tells us it has
    // more to work with.
    ['loadedmetadata', 'loadeddata', 'canplay'].forEach(function (type) {
      video.addEventListener(type, function () {
        if (video.paused && !stoppedByVisitor) attempt();
      });
    });

    if (button) {
      button.addEventListener('click', function () {
        if (video.paused) {
          stoppedByVisitor = false;
          attempt();
        } else {
          stoppedByVisitor = true;
          releaseGesture();
          video.pause();
        }
      });
    }

    if (video.paused) attempt();
    sync();
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-hero-850-video]').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor re-renders sections in place, so re-bind on load.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
